import { ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
// TODO: remove comment in isRouteDisabled to load CompanyClubContext if route is disabled
import { useLocation, /* useMatch, */ useNavigate } from 'react-router-dom';
import { shouldShowFloorplanV2 } from '@components/floorplan-v2/should-show-floorplan-v2';
import { /* COMPANY_SETTINGS_ROUTE, */ SELECT_VENUE_ROUTE, USER_SETTINGS_ROUTE } from '@constants/routes';
import { AuthContext } from '@contexts/auth/auth-context';
import { CompanyAuthContext } from '@contexts/company-auth-context/company-auth-context';
import { CompanyClubContextValue, CompanyClubState } from '@contexts/companyClub/companyClub.type';
import { CompanyClubContext } from '@contexts/companyClub/companyClub-context';
import { FeatureFlagContext } from '@contexts/featureFlag/featureFlag-context';
import { UserContext } from '@contexts/user/user-context';
import { Club } from '@domain/club';
import { Company } from '@domain/company';
import { useGetCompanyClub } from '@hooks/company-club/use-get-company-club';
import { usePostHog } from '@posthog/react';
import { clearCompanyContext, identifyCompanyGroupProperties } from '@posthog-app/company-identification';
import { useFeatureFlag, useFeatureFlagPayload } from '@posthog-app/use-feature-flag';

import { companyClubEmptyState } from './utils';

export const CompanyClubProvider = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { setCompanyAuthId, setCompanyClubId } = useContext(CompanyAuthContext);
  const { isSelectVenueActive, testCompanyIds, externalPlatformEnabledIds } = useContext(FeatureFlagContext);
  const { isAuthenticated } = useContext(AuthContext);
  const { userState } = useContext(UserContext);
  const posthog = usePostHog();
  const companyId = pathname.split('/')[1];
  const clubSlug = pathname.split('/')[2];

  const isTestVenueCompany = (testCompanyIds as string).split(',').includes(companyId);
  const isExternalPlatformEnabledForCompany = (externalPlatformEnabledIds as string).split(',').includes(companyId);
  // TODO: remove comment in isRouteDisabled to load CompanyClubContext if route is disabled
  // const isRouteDisabled = useMatch(COMPANY_SETTINGS_ROUTE);
  const isCompanyClubContextEnabled = !!companyId && !companyId.includes(':') && !!clubSlug && !clubSlug.includes(':');

  const [companyClubState, setCompanyClubState] = useState<CompanyClubState>({
    id: '',
    legacyId: '',
    isSiaeImportLimited: false,
  });

  const [companyState, setCompanyState] = useState<Company>({
    id: '',
    legacyId: '',
    name: '',
    commercialName: '',
    defaultChannel: {
      id: '',
      name: '',
      slug: '',
    },
  });

  const [clubState, setClubState] = useState<Club>({
    id: '',
    legacyId: '',
    coverUrl: '',
    logoUrl: '',
    name: '',
    slug: '',
    coordinates: {
      latitude: 0,
      longitude: 0,
    },
    city: {
      country: {
        id: '',
        name: '',
        isoCode: '',
        currency: '',
      },
      id: '',
      legacyId: '',
      coverUrl: '',
      name: '',
      slug: '',
      timezone: '',
    },
  });

  // Per-company gate for the interactive floorplan editor (V2), sourced from PostHog.
  // Derived here next to the sibling per-company flags so the combine rule lives in one place.
  const isFloorplanV2Active = shouldShowFloorplanV2(
    useFeatureFlag('floorplan-interactive-editor'),
    useFeatureFlagPayload('floorplan-interactive-editor'),
    companyState.legacyId
  );

  // Gates the bottle-service "Extra guests" field — a simple on/off PostHog flag (no per-company payload).
  const isExtraGuestsForBottleServiceActive = useFeatureFlag('extra-guests-for-bottle-services');

  // Phase-2 gates for the new offer drawers: features held back from the 100% form rollout.
  // Simple on/off PostHog flags. The galleries replaced the build-time SSM params
  // `show-admission-image-gallery` / `show-addon-image-gallery`, which were global per environment.
  // The admission gallery flag also covers the pass and admission-preset galleries (same chassis).
  const isAdmissionImageGalleryActive = useFeatureFlag('admission-image-gallery');
  const isAddonImageGalleryActive = useFeatureFlag('addon-image-gallery');
  const isAdmissionIncludeDrinksActive = useFeatureFlag('admission-include-drinks');
  const isAddonTermsAndConditionsLinkActive = useFeatureFlag('addon-terms-and-conditions-link');

  // Gates the widget Checkout "Enable sign-in" section and the SIAE booking-plugin share link.
  // Derived once here so the flag key isn't read inline across consumers (widget form, admission card).
  const isWidgetSignInEnabled = useFeatureFlag('widget-config-enable-sign-in');

  // Gates the widget Layout resale toggles (show the resale section, and include listings from
  // other channels). Targeting lives in PostHog, so no payload is read here. Flag OFF must stay
  // inert, not merely hidden: the backend half (event-service #380) ships separately, so with the
  // flag off no resale key is seeded into the form defaults and none reaches the save payload.
  const isWidgetResaleActive = useFeatureFlag('widget-config-resale-section');
  // Gates the widget install block's embed-code generator (Full widget / Single event modes and the
  // event picker behind the offers snippet). Targeting lives in PostHog, so no payload is read here.
  // Flag OFF restores the previous hardcoded snippet verbatim and fires no event-list request, so the
  // UI can ship ahead of widget-b2c #219 without advertising a `data-mode` the widget cannot parse.
  const isWidgetEmbedGeneratorActive = useFeatureFlag('widget-config-embed-code-generator');

  // Gates the whole Secret Location UI (toggle, dropdowns, LocationCard). Targeting lives in PostHog
  // (company group + internal cohort), so no payload is read here — the flag arrives already resolved
  // for the current company and is fail-closed while it loads. Replaced VITE_SHOW_SECRET_LOCATION,
  // which was global per environment (B2B-2059).
  const isSecretLocationActive = useFeatureFlag('event-secret-location');

  // Gates the whole organizer public-profile block in Company settings -> Info (description +
  // translations, region, music tags, vibes, gallery, publish toggle). Targeting lives in PostHog,
  // so no payload is read here. Flag OFF must stay inert, not merely hidden: the block ships to
  // production ahead of the B2C organizer page, so the option queries behind it are `enabled`-gated
  // and no new form state is registered.
  const isOrganizerPublicProfileActive = useFeatureFlag('organizer-public-profile');

  // Gates the whole Demographics section of the Event Report. Targeting lives in PostHog, so no
  // payload is read here. It exists so the section's tickets can merge to develop one at a time
  // while the blocks are still stand-in skeletons: flag OFF renders nothing at all, not a hidden
  // section, so no placeholder reaches a release cut and no demographics query is fired.
  const isEventReportDemographicsActive = useFeatureFlag('event-report-demographics');

  // Gates both delayed-delivery surfaces at once: the company Event Defaults tab (plus its route) and
  // the Delayed Delivery section of the event Settings tab. One flag on purpose: a company default the
  // organizer cannot override per event would be worse than either state. Targeting lives in PostHog.
  const isDelayedDeliveryActive = useFeatureFlag('delayed-delivery-settings');

  const setEmptyCompanyClubState = useCallback(() => {
    setClubState(companyClubEmptyState.clubState);
    setCompanyClubState(companyClubEmptyState.companyClubState);
    setCompanyState(companyClubEmptyState.companyState);
  }, []);

  const {
    data: companyClub,
    error: companyClubError,
    isLoading: isLoadingCompanyClub,
  } = useGetCompanyClub({
    companyId,
    clubSlug,
    options: {
      // TODO: remove comment in isRouteDisabled to load CompanyClubContext if route is disabled
      enabled: isCompanyClubContextEnabled && !!userState, // && !isRouteDisabled,
    },
  });

  const shouldNavigateToSelectVenue =
    userState &&
    !isCompanyClubContextEnabled &&
    pathname !== SELECT_VENUE_ROUTE &&
    pathname !== USER_SETTINGS_ROUTE &&
    isSelectVenueActive;

  useEffect(() => {
    if (shouldNavigateToSelectVenue) {
      navigate(SELECT_VENUE_ROUTE);
    }
  }, [shouldNavigateToSelectVenue, navigate]);

  useEffect(() => {
    if (!!companyClubError && companyClubError.status === 403 && isSelectVenueActive && isAuthenticated) {
      navigate(SELECT_VENUE_ROUTE);
    }
  }, [companyClubError, navigate, isSelectVenueActive, isAuthenticated]);

  useEffect(() => {
    if (companyClub && isCompanyClubContextEnabled && !companyClubError?.status) {
      const { id, legacyId, isSiaeImportLimited } = companyClub;
      setCompanyClubState({
        id,
        legacyId,
        isSiaeImportLimited,
      });
      if (companyClub?.club) setClubState(companyClub.club);
      if (companyClub?.company) {
        setCompanyState(companyClub.company);
        const legacyCompanyId = Number.parseInt(companyClub.company.legacyId);
        // Impersonated sessions included on purpose: the company group is what
        // company-scoped feature flags resolve against, and capture is off for the whole
        // session (syncImpersonationCapture), so $groupidentify is never sent.
        if (posthog && !userState?.isSuperAdmin && !Number.isNaN(legacyCompanyId)) {
          identifyCompanyGroupProperties(posthog, {
            companyId: legacyCompanyId,
            name: companyClub.company.name,
            commercialName: companyClub.company.commercialName,
          });
        }
      }
      setCompanyAuthId(companyClub.company.id);
      setCompanyClubId(companyClub.club.id);
    } else if (!isCompanyClubContextEnabled) {
      setEmptyCompanyClubState();
      if (posthog) clearCompanyContext(posthog);
    }
  }, [
    companyId,
    clubSlug,
    companyClub,
    isCompanyClubContextEnabled,
    setEmptyCompanyClubState,
    userState?.isSuperAdmin,
    isSelectVenueActive,
    companyClubError,
    setCompanyAuthId,
    setCompanyClubId,
    posthog,
  ]);

  const memoizedContextValue = useMemo<CompanyClubContextValue>(
    () => ({
      companyClubState,
      companyState,
      clubState,
      setCompanyClubState,
      setCompanyState,
      setClubState,
      setEmptyCompanyClubState,
      isTestVenueCompany,
      isExternalPlatformEnabledForCompany,
      isFloorplanV2Active,
      isExtraGuestsForBottleServiceActive,
      isAdmissionImageGalleryActive,
      isAddonImageGalleryActive,
      isAdmissionIncludeDrinksActive,
      isAddonTermsAndConditionsLinkActive,
      isWidgetSignInEnabled,
      isWidgetResaleActive,
      isWidgetEmbedGeneratorActive,
      isSecretLocationActive,
      isOrganizerPublicProfileActive,
      isEventReportDemographicsActive,
      isDelayedDeliveryActive,
      isLoadingCompanyClub,
    }),
    [
      companyClubState,
      companyState,
      clubState,
      setCompanyClubState,
      setCompanyState,
      setClubState,
      setEmptyCompanyClubState,
      isTestVenueCompany,
      isExternalPlatformEnabledForCompany,
      isFloorplanV2Active,
      isExtraGuestsForBottleServiceActive,
      isAdmissionImageGalleryActive,
      isAddonImageGalleryActive,
      isAdmissionIncludeDrinksActive,
      isAddonTermsAndConditionsLinkActive,
      isWidgetSignInEnabled,
      isWidgetResaleActive,
      isWidgetEmbedGeneratorActive,
      isSecretLocationActive,
      isOrganizerPublicProfileActive,
      isEventReportDemographicsActive,
      isDelayedDeliveryActive,
      isLoadingCompanyClub,
    ]
  );

  return <CompanyClubContext.Provider value={memoizedContextValue}>{children}</CompanyClubContext.Provider>;
};
